import { ColorSquare } from '@/components/Analysis/DeckPlan/ColorLegend/ColorLegend';
import { abyssColors } from '@/theme/colors';
import { Box, Stack, Typography } from '@mui/material';
import Fade from '@mui/material/Fade';
import { Cell, Pie, PieChart, Tooltip as ChartToolTip, TooltipProps } from 'recharts';
import { ValueType, NameType } from 'recharts/types/component/DefaultTooltipContent';
import { Widget } from '../styles';

type Props = {
  widgetWidth: string | number;
  loading: boolean;
  chartScale: number;
  data:
    | {
        count: number | null | undefined;
        key: string | null | undefined;
      }[]
    | undefined;
  chartSize: {
    outerRadius: number;
    innerRadius: number;
  };
  colorMapper: (key: string) => string;
  title: string;
  centerText: string;
  centerValue: string | number;
  legendKeys: string[];
};

const CustomTooltip = (parameters: TooltipProps<ValueType, NameType>) => {
  const { payload } = parameters;
  const label = payload?.[0]?.payload?.key;
  const value = payload?.[0]?.value;

  if (!label || !value || label === 'N/A') return <></>;

  return (
    <Box
      sx={{
        p: '10px 8px',
        borderRadius: '2px',
        background: 'white',
        boxShadow: '0px 2px 8px 0px rgba(0, 0, 0, 0.15)',
        color: 'primary.main',
        fontSize: '14px',
      }}
    >{`${value} ${label}`}</Box>
  );
};

export const PieChartWidget = ({
  widgetWidth,
  loading,
  chartScale,
  data,
  chartSize,
  colorMapper,
  title,
  centerText,
  centerValue,
  legendKeys,
}: Props) => {
  return (
    <Widget loading={loading} width={widgetWidth}>
      {!loading && (
        <Fade in>
          <Stack spacing={2}>
            <Typography
              lineHeight={1}
              fontSize="1.4rem"
              fontWeight={500}
              color={abyssColors.primary[500]}
            >
              {title}
            </Typography>
            <Box display="flex" justifyContent="center">
              <Box width={150 * chartScale} height={150 * chartScale} position={'relative'}>
                <PieChart width={150 * chartScale} height={150 * chartScale} style={{ zIndex: 1 }}>
                  <Pie
                    startAngle={90}
                    endAngle={450}
                    data={data}
                    dataKey="count"
                    nameKey="key"
                    cx="50%"
                    cy="50%"
                    innerRadius={chartSize.innerRadius}
                    outerRadius={chartSize.outerRadius}
                  >
                    {data?.map((item, index) => (
                      <Cell key={`${item?.key}-${index}`} fill={colorMapper(item?.key ?? '')} />
                    ))}
                  </Pie>
                  <ChartToolTip
                    allowEscapeViewBox={{ x: true, y: true }}
                    content={<CustomTooltip />}
                  />
                </PieChart>
                <Stack
                  direction="column"
                  justifyContent="center"
                  alignItems="center"
                  position="absolute"
                  top="50%"
                  left="50%"
                  sx={{ transform: 'translate(-50%, -50%)' }}
                >
                  <>
                    <Typography
                      color={abyssColors.primary[300]}
                      fontWeight={400}
                      fontSize="1.2rem"
                      sx={{ textWrap: 'nowrap' }}
                    >
                      {centerText}
                    </Typography>
                    <Typography
                      color={abyssColors.primary[500]}
                      fontWeight={500}
                      fontSize="2.4rem"
                      data-testid={`pie-${centerText.toLowerCase()}-count`}
                    >
                      {centerValue}
                    </Typography>
                  </>
                </Stack>
              </Box>
            </Box>
            <Stack
              direction="row"
              alignContent="center"
              gap="1.6rem"
              rowGap="0.1rem"
              flexWrap="wrap"
              sx={{ height: '30px' }}
            >
              {legendKeys?.map((key) => (
                <Stack direction="row" alignItems="baseline" gap="0.4rem" key={key}>
                  <ColorSquare color={colorMapper(key)} />
                  <Typography variant="caption">{key}</Typography>
                </Stack>
              ))}
            </Stack>
          </Stack>
        </Fade>
      )}
    </Widget>
  );
};
